// Client-side cache utilities with localStorage and background refresh
interface CacheItem<T> {
  data: T;
  timestamp: number;
  ttl: number;
  lastFetch?: number;
}

export class ClientCache {
  private storagePrefix = "alter_expo_cache_";
  private backgroundRefreshThreshold = 0.7; // Refresh when 70% of TTL has passed

  // Store data in localStorage with TTL
  set<T>(key: string, data: T, ttlMinutes = 5): void {
    const cacheItem: CacheItem<T> = {
      data,
      timestamp: Date.now(),
      ttl: ttlMinutes * 60 * 1000,
      lastFetch: Date.now(),
    };

    try {
      localStorage.setItem(this.storagePrefix + key, JSON.stringify(cacheItem));
      console.log(`ClientCache SET: ${key}, TTL: ${ttlMinutes}min`);
    } catch (error) {
      console.warn("ClientCache SET failed (localStorage full?):", error);
    }
  }

  // Get data from localStorage, return null if expired
  get<T>(key: string): T | null {
    try {
      const stored = localStorage.getItem(this.storagePrefix + key);
      if (!stored) {
        console.log(`ClientCache MISS: ${key} - not found`);
        return null;
      }

      const item: CacheItem<T> = JSON.parse(stored);
      const now = Date.now();
      const age = now - item.timestamp;

      if (age > item.ttl) {
        console.log(
          `ClientCache MISS: ${key} - expired (age: ${Math.round(age / 1000)}s)`
        );
        this.delete(key);
        return null;
      }

      console.log(`ClientCache HIT: ${key} - age: ${Math.round(age / 1000)}s`);
      return item.data;
    } catch (error) {
      console.warn(`ClientCache GET error for ${key}:`, error);
      return null;
    }
  }

  // Check if data should be refreshed in background (70% of TTL passed)
  shouldBackgroundRefresh(key: string): boolean {
    try {
      const stored = localStorage.getItem(this.storagePrefix + key);
      if (!stored) return false;

      const item: CacheItem<any> = JSON.parse(stored);
      const now = Date.now();
      const age = now - item.timestamp;
      const refreshThreshold = item.ttl * this.backgroundRefreshThreshold;

      const shouldRefresh = age > refreshThreshold;
      if (shouldRefresh) {
        console.log(
          `ClientCache: Background refresh needed for ${key} (age: ${Math.round(
            age / 1000
          )}s, threshold: ${Math.round(refreshThreshold / 1000)}s)`
        );
      }
      return shouldRefresh;
    } catch (error) {
      return false;
    }
  }

  // Update last fetch timestamp to prevent multiple background requests
  markAsRefreshing(key: string): void {
    try {
      const stored = localStorage.getItem(this.storagePrefix + key);
      if (!stored) return;

      const item: CacheItem<any> = JSON.parse(stored);
      item.lastFetch = Date.now();
      localStorage.setItem(this.storagePrefix + key, JSON.stringify(item));
    } catch (error) {
      console.warn(`ClientCache markAsRefreshing error for ${key}:`, error);
    }
  }

  delete(key: string): void {
    try {
      localStorage.removeItem(this.storagePrefix + key);
      console.log(`ClientCache DELETE: ${key}`);
    } catch (error) {
      console.warn(`ClientCache DELETE error for ${key}:`, error);
    }
  }

  clear(): void {
    try {
      const keysToDelete = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.storagePrefix)) {
          keysToDelete.push(key);
        }
      }
      keysToDelete.forEach((key) => localStorage.removeItem(key));
      console.log(`ClientCache CLEAR: Removed ${keysToDelete.length} items`);
    } catch (error) {
      console.warn("ClientCache CLEAR error:", error);
    }
  }

  // Get cache statistics
  getStats() {
    const keys: string[] = [];
    const sizes: number[] = [];

    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.storagePrefix)) {
          keys.push(key.replace(this.storagePrefix, ""));
          const value = localStorage.getItem(key);
          sizes.push(value ? value.length : 0);
        }
      }
    } catch (error) {
      console.warn("ClientCache getStats error:", error);
    }

    return {
      totalKeys: keys.length,
      keys,
      totalSize: sizes.reduce((sum, size) => sum + size, 0),
    };
  }
}

export const clientCache = new ClientCache();

// Cache keys for different data types
export const CLIENT_CACHE_KEYS = {
  ADMIN_DATA: "admin_data",
  ORGANIZER_DATA: (id: string) => `organizer_data_${id}`,
  STANDHOUDER_DATA: (id: string) => `standhouder_data_${id}`,
  SIZES: "sizes",
  SUPPLIERS: "suppliers",
  ORGANIZERS: "organizers",
} as const;
